import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
/**
 * CompleteInvite
 * --------------
 * What:
 *   Landing page for passwordless email‑link completion.
 *
 * Flow:
 *   - Verify the URL is an Auth email‑link.
 *   - Same device: use localStorage('emailForSignIn') and complete automatically.
 *   - Different device: prompt the user to re‑enter their email to avoid session fixation.
 *   - On success: navigate to post‑account route (dashboard or next form).
 *
 * Analytics:
 *   - Errors are reported via useRmsAnalytics for visibility in DebugView.
 *
 * Notes:
 *   - We never pass email in the link; we keep any stored email only on the same device.
 *   - The server 'rmsCompleteInvitation' finalizes membership and referral conversion.
 */
import { getOrgConfig } from '../utils/orgResolution';
import { auth } from '../../firebase';
import { isSignInWithEmailLink, signInWithEmailLink, onAuthStateChanged } from 'firebase/auth';
import { apiCompleteReferrerAccountSetup } from '../utils/rmsApi';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import useRmsAnalytics from '../hooks/useRmsAnalytics';
import providerLogo from '../assets/images/logos/lcs_logo.png';

export default function CompleteInvite() {
  const { orgId } = useMemo(() => getOrgConfig(), []);
  const analytics = useRmsAnalytics(orgId);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [needsEmail, setNeedsEmail] = useState(false);
  // True when we can't complete here (no email-link params and no session) — we
  // then point the user at sign-in instead of dead-ending on the spinner.
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);
  const [completed, setCompleted] = useState(false);
  // Guards against double finalisation: the proceed() effect and the
  // onAuthStateChanged effect can both fire before `completed` flips, since it
  // is only set after the awaited backend call (TD-19). A ref updates
  // synchronously, so the second caller bails out immediately.
  const finalizingRef = useRef(false);

  const completeEmailLink = useCallback(async (emailToUse) => {
    try {
      setBusy(true);
      await signInWithEmailLink(auth, emailToUse, window.location.href);
      try { window.localStorage.removeItem('emailForSignIn'); } catch (_) {}
      // After sign-in, fall through to complete invitation on this page
      setBusy(false);
    } catch (e) {
      setError(e?.message || 'Failed to complete sign-in. The link may be invalid or expired.');
      analytics.error({ where: 'completeInvite', code: e?.code || 'unknown', message: String(e?.message || e) });
      setBusy(false);
    }
  }, [analytics]);

  const finishInvitation = useCallback(async () => {
    // Run exactly once, even if both effects fire before `completed` flips.
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    try {
      setBusy(true);
      const params = new URLSearchParams(location.search);
      const intentId = params.get('intentId');
      if (!intentId || !orgId) {
        setError('Missing invitation context.');
        setBusy(false);
        finalizingRef.current = false;
        return;
      }
      // Use the consolidated endpoint that supports both school and guardian referrers
      const res = await apiCompleteReferrerAccountSetup({ orgId, intentId });
      // The backend just set custom claims (organisation, accountType, etc.)
      // on the server. Force a token refresh so the client picks them up
      // before navigating — otherwise useAuth() on the dashboard would
      // still have the stale pre-claims token.
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }
      setCompleted(true);
      navigate('/rms/dashboard');
      return res;
    } catch (e) {
      setError(e?.message || 'Failed to finalize invitation.');
      analytics.error({ where: 'completeInvite.finish', code: e?.code || 'unknown', message: String(e?.message || e) });
      setBusy(false);
      finalizingRef.current = false; // allow a retry after a transient failure
      return null;
    }
  }, [analytics, navigate, orgId, location.search]);

  useEffect(() => {
    const href = window.location.href;
    const isLink = isSignInWithEmailLink(auth, href);
    const proceed = async () => {
      // Case 1: Arrive via Firebase email-link (rare with continueUrl)
      if (isLink) {
        let stored = '';
        try { stored = window.localStorage.getItem('emailForSignIn') || ''; } catch (_) {}
        if (stored) {
          setNeedsEmail(false);
          await completeEmailLink(stored);
        } else {
          setNeedsEmail(true);
          setBusy(false);
          return;
        }
      }
      // Case 2: Already signed in (arrived via verify-action → continueUrl)
      if (auth.currentUser && !completed) {
        await finishInvitation();
        return;
      }
      // Neither an email-link nor a signed-in session: we can't complete here.
      // Surface a clear next step (sign in) instead of a silent spinner — unless
      // an email-link attempt already set an error to display.
      setBusy(false);
      if (!isLink && !auth.currentUser) {
        setNeedsSignIn(true);
      }
    };
    proceed();
  }, [completeEmailLink, finishInvitation, completed]);

  // Also react to auth state changes (e.g., when arriving from verify-action continueUrl)
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user && !completed) {
        await finishInvitation();
      }
    });
    return () => unsub();
  }, [finishInvitation, completed]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
      <div className="sticky top-0 left-0 right-0 bg-white z-30 relative" style={{ boxShadow: '0 24px 36px rgba(255,255,255,0.95), 0 12px 20px rgba(255,255,255,0.85)' }}>
        <div className="w-full px-6 md:px-10 pt-10">
          <div className="w-full md:w-1/2 mx-auto mb-8 flex flex-col items-end gap-2">
            <img src={providerLogo} alt="LCS logo" className="h-20 w-auto opacity-90" />
          </div>
        </div>
      </div>
      <div className="h-12" />
      <div className="flex-grow w-full px-6 md:px-10 pb-28 text-left">
        <div className="w-full md:w-1/2 mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Completing Sign‑in…</h1>
          {busy && (
            <p className="text-gray-700">Please wait while we complete your secure sign‑in.</p>
          )}
          {!busy && needsSignIn && (
            <div>
              <p className="text-gray-700 mb-3">
                We couldn't complete your sign‑in automatically. This can happen if the link
                was opened in a different browser or has already been used. Please sign in to
                continue your referral.
              </p>
              <button
                onClick={() => {
                  const params = new URLSearchParams(location.search);
                  const intentId = params.get('intentId');
                  const back = intentId
                    ? `/rms/complete-invite?intentId=${encodeURIComponent(intentId)}`
                    : '/rms/dashboard';
                  navigate(`/rms/sign-in?orgId=${encodeURIComponent(orgId)}&continue=${encodeURIComponent(back)}`);
                }}
                className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-primary hover:bg-primary/90"
              >
                Go to sign‑in
              </button>
            </div>
          )}
          {!busy && needsEmail && (
            <div>
              <p className="text-gray-700 mb-3">For security, please confirm the email address the link was sent to.</p>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                <input
                  type="email"
                  className="w-full p-3 border rounded-lg bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent border-gray-200"
                  style={{ '--tw-ring-color': '#e7c22f', '--tw-ring-opacity': '0.5' }}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
              <button
                onClick={() => email ? completeEmailLink(email) : null}
                disabled={!email}
                className={`w-full py-3 px-4 rounded-lg font-semibold text-white ${email ? 'bg-primary hover:bg-primary/90' : 'bg-gray-300 text-gray-600 cursor-not-allowed'}`}
              >
                Continue
              </button>
            </div>
          )}
          {!!error && (
            <div className="mt-4 text-sm text-red-600">{error}</div>
          )}          
        </div>
      </div>
    </div>
  );
}


